import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtom } from 'jotai';

import type { PersistUserValidationsRequest } from '../@types/persistUserValidations';
import {
  FindingTypesSchema,
  FindingValidations,
  RblToothSides,
} from '../@types/persistUserValidations';
import { selectedVisitAtom } from '../app/appHeader/imageViewerMenu/atoms';
import {
  AREA_OF_CONCERN,
  CARIES,
  PERIAPICAL_RADIOLUCENCY,
  RADIOGRAPHIC_BONELOSS,
  RESTORATIVE_ADJACENT_LUCENCY,
} from '../constants';
import type { FindingObject } from '../modules/annotator/types';
import { makeAPIRequestV2 } from '../utils/api';

import { endpoints } from './endpoints';
import { usePreferences } from './usePreferences';

interface UseUpdateUserValidationParams extends PersistUserValidationsRequest {
  findingId: string;
  imageId: string;
}

export const useUpdateUserValidation = () => {
  const { data: prefs } = usePreferences();

  const queryClient = useQueryClient();
  const [selectedEstimatedVisitId] = useAtom(selectedVisitAtom);

  return useMutation(
    (userValidationData: UseUpdateUserValidationParams) => {
      const { findingId, imageId, ...userValidationRequestBody } =
        userValidationData;

      if (prefs?.enableLatestImagesV2) {
        return makeAPIRequestV2(
          `/v2/${endpoints.images}/${imageId}/${endpoints.annotations}/${findingId}/${endpoints.validation}`,
          { data: userValidationRequestBody },
          'PATCH',
        );
      }

      return makeAPIRequestV2(
        `/${endpoints.images}/${imageId}/${endpoints.annotations}/${findingId}/${endpoints.validation}`,
        { data: userValidationRequestBody },
        'PATCH',
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          Number(selectedEstimatedVisitId),
          'analyses',
          'segmentations',
        ]);
      },
    },
  );
};

export const generateValidationRequest = (
  finding: FindingObject,
  imageId: string,
  userValidation: FindingValidations,
  // @ts-expect-error TS(2366): Function lacks ending return statement and return ... Remove this comment to see the full error message
): UseUpdateUserValidationParams => {
  const { toothNumber, side, type, id, points, annotationId } = finding;

  const data = {
    validationStatus: FindingValidations[userValidation],
    findingType: FindingTypesSchema[type as keyof typeof FindingTypesSchema],
  };
  if (
    type === CARIES ||
    type === PERIAPICAL_RADIOLUCENCY ||
    type === AREA_OF_CONCERN ||
    type === RESTORATIVE_ADJACENT_LUCENCY
  ) {
    const boundingBoxRequest = {
      ...data,
      findingId: id ?? annotationId,
      imageId,
    };
    return boundingBoxRequest;
  }
  if (type === RADIOGRAPHIC_BONELOSS) {
    const rblPointId = points[0]?.id.split('.')[0];
    const rblRequestData = {
      ...data,
      toothNumber,
      // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
      rblToothSurface: RblToothSides[side],
      findingId: rblPointId,
      imageId,
    };
    return rblRequestData;
  }
};
